'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePackagesParams } from '@hooks/use-packages-params';
import { useSearchHotelStore } from '@store/search-hotel-store';
import {
  Button,
  Close,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Skeleton,
} from '@v2/ui';
import { useHotelDetails } from '../../ViewHotel/hooks/use-hotel-details';
import { useGetHotels } from '../hooks/use-hotels-list';
import { useHotelPriceLabel } from '../hooks/use-hotel-price-label';
import { HotelStars } from './HotelContent/HotelCard';
import { GuestRatingBadge, TrustSignalsList } from './TrustSignals';
import { HOTEL_AMENETIES } from './data';
import type { TrustSignal } from '@interfaces/expedia/property';

export function HotelPreview({ proximityName }: { proximityName?: string }) {
  const selected = useSearchHotelStore((state) => state.hotelDrawerSelected);
  return (
    <Dialog
      open={selected.isOpen}
      onOpenChange={(open) => {
        if (!open)
          useSearchHotelStore.getState().setHotelSelectedInMap(null, false);
      }}
    >
      {selected.isOpen && selected.hotelId !== null && (
        <PreviewContent
          key={selected.hotelId}
          hotelId={selected.hotelId}
          proximityName={proximityName}
        />
      )}
    </Dialog>
  );
}

function PreviewContent({
  hotelId,
  proximityName,
}: {
  hotelId: number;
  proximityName?: string;
}) {
  const { data, error, isLoading, mutate } = useHotelDetails(hotelId);
  const { eventId, eventLink, packageType } = usePackagesParams();
  const isBundle = packageType === 'packages';
  const { data: hotels, lowestHotelPrice } = useGetHotels();
  const summary = hotels.find((hotel) => hotel.id === hotelId);
  const hotel = data?.propertyDetails ?? summary;
  const label = useHotelPriceLabel()(
    summary?.price ?? data?.propertyDetails.minimumTotalPrice ?? NaN,
    lowestHotelPrice
  );
  const distance = summary?.distance;

  const minPriceInHotels = useMemo(() => {
    let min = Infinity;
    let id: number | null = null;
    for (const h of hotels ?? []) {
      if (
        typeof h.price === 'number' &&
        Number.isFinite(h.price) &&
        h.price > 0
      ) {
        if (h.price < min) {
          min = h.price;
          id = h.id;
        }
      }
    }
    return id;
  }, [hotels]);

  const isCheapest = summary ? summary.id === minPriceInHotels : false;

  const rawSignals =
    data?.propertyDetails?.trustSignals?.length
      ? data.propertyDetails.trustSignals
      : summary?.trustSignals;

  const trustSignals = useMemo(() => {
    const list: TrustSignal[] = rawSignals ? [...rawSignals] : [];
    if (isCheapest && !list.includes('cheapest')) {
      const withoutBestPrice = list.filter((s) => s !== 'best_price');
      return ['cheapest' as TrustSignal, ...withoutBestPrice];
    }
    return list;
  }, [rawSignals, isCheapest]);

  const effectiveGuestRating =
    data?.propertyDetails?.guestRating ??
    data?.propertyDetails?.guestRate ??
    summary?.guestRating ??
    summary?.guestRate;

  const effectiveGuestRatingCount =
    data?.propertyDetails?.guestRatingCount ??
    data?.propertyDetails?.guestRateCount ??
    summary?.guestRatingCount ??
    summary?.guestRateCount;

  const [photo, setPhoto] = useState(0);
  const thumbnailsRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const images = data?.propertyDetails.images?.length
    ? data.propertyDetails.images
    : hotel?.image
      ? [hotel.image]
      : [];
  const index = Math.min(photo, Math.max(images.length - 1, 0));

  useEffect(() => {
    const container = thumbnailsRef.current;
    if (!container) return;
    const activeThumbnail = container.children.item(index);
    if (!(activeThumbnail instanceof HTMLElement)) return;

    const containerWidth = container.offsetWidth;
    const itemLeft = activeThumbnail.offsetLeft;
    const itemWidth = activeThumbnail.offsetWidth;
    const targetScrollLeft = itemLeft - containerWidth / 2 + itemWidth / 2;
    const prefersReducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (typeof container.scrollTo === 'function') {
      container.scrollTo({
        left: targetScrollLeft,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    } else {
      container.scrollLeft = targetScrollLeft;
    }
  }, [index, images.length]);

  const rawDescription = data?.propertyDetails?.descriptions?.[0];
  const description =
    typeof rawDescription === 'string'
      ? rawDescription.replace(/<[^>]*>/g, '').trim()
      : null;

  return (
    <DialogContent
      showCloseButton={false}
      onOpenAutoFocus={() => {
        const activeElement = document.activeElement;
        if (activeElement instanceof HTMLElement)
          returnFocusRef.current = activeElement;
      }}
      onCloseAutoFocus={(event) => {
        event.preventDefault();
        const opener = returnFocusRef.current;
        const target =
          opener?.isConnected && opener !== document.body
            ? opener
            : document.querySelector<HTMLElement>(`#hotel-card-${hotelId} a`);
        target?.focus();
      }}
      aria-busy={isLoading}
      className="z-[60] max-h-[90dvh] w-[calc(100%-24px)] gap-0 overflow-hidden p-0 md:max-h-[95dvh] md:max-w-[1000px]"
    >
      <DialogClose
        type="button"
        className="absolute right-3 top-3 z-20 flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white/90 text-gray-500 shadow-sm transition hover:bg-white hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 md:right-4 md:top-4"
        aria-label="Close dialog"
      >
        <Close size="16" aria-hidden="true" />
      </DialogClose>

      <div className="grid max-h-[90dvh] overflow-y-auto md:max-h-[95dvh] md:grid-cols-2">
        <div className="flex min-w-0 flex-col bg-gray-50">
          <div
            className="relative aspect-video w-full overflow-hidden bg-gray-100 md:aspect-[4/3]"
            role="group"
            aria-label="Hotel photos"
          >
            {images[index] ? (
              <Image
                src={images[index]}
                alt={`${hotel?.name ?? 'Hotel'}, photo ${index + 1}`}
                fill
                sizes="(min-width: 768px) 500px, 100vw"
                className="object-cover"
              />
            ) : (
              <Skeleton className="h-full w-full rounded-none" />
            )}
            {images.length > 1 && (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="absolute left-3 top-1/2 z-10 h-10 w-10 -translate-y-1/2 rounded-full bg-white/90 p-0 text-gray-800 shadow-md hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 motion-safe:transition-transform motion-safe:hover:scale-105"
                  aria-label="Previous hotel photo"
                  onClick={() =>
                    setPhoto((index + images.length - 1) % images.length)
                  }
                >
                  ←
                </Button>
                <span
                  className="absolute bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-full bg-gray-900/80 px-3 py-1 text-xs text-white backdrop-blur-sm"
                  aria-live="polite"
                >
                  {index + 1} / {images.length}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="absolute right-3 top-1/2 z-10 h-10 w-10 -translate-y-1/2 rounded-full bg-white/90 p-0 text-gray-800 shadow-md hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 motion-safe:transition-transform motion-safe:hover:scale-105"
                  aria-label="Next hotel photo"
                  onClick={() => setPhoto((index + 1) % images.length)}
                >
                  →
                </Button>
              </>
            )}
          </div>

          {isLoading && images.length <= 1 ? (
            <div
              className="flex gap-2 overflow-x-auto p-3"
              aria-label="Loading hotel photos"
              role="status"
            >
              <span className="sr-only">Loading hotel photos…</span>
              {[0, 1, 2, 3, 4].map((item) => (
                <Skeleton
                  key={item}
                  aria-hidden="true"
                  className="h-16 w-24 shrink-0 rounded-lg"
                />
              ))}
            </div>
          ) : images.length > 1 ? (
            <div
              ref={thumbnailsRef}
              className="flex gap-2 overflow-x-auto p-3"
              aria-label="Choose a hotel photo"
            >
              {images.map((image, i) => (
                <button
                  type="button"
                  key={`${image}-${i}`}
                  aria-label={`Show photo ${i + 1}`}
                  aria-pressed={i === index}
                  onClick={() => setPhoto(i)}
                  className={`relative h-16 w-24 shrink-0 overflow-hidden rounded-lg border-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 ${i === index ? 'border-primary-500' : 'border-transparent'}`}
                >
                  <Image
                    src={image}
                    alt=""
                    fill
                    sizes="96px"
                    className="object-cover"
                  />
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-4 p-5 md:p-7">
          {trustSignals && trustSignals.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              <TrustSignalsList signals={trustSignals} size="sm" />
            </div>
          )}
          <DialogTitle className="pr-10 text-xl font-bold md:text-2xl">
            {hotel?.name ? (
              hotel.name
            ) : (
              <>
                <span className="sr-only">Hotel preview</span>
                <Skeleton aria-hidden="true" className="h-7 w-3/4" />
              </>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Photos, amenities, and hotel selection for your stay.
          </DialogDescription>
          {hotel ? (
            <>
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                <HotelStars rating={hotel.rating} />
                {effectiveGuestRating != null && effectiveGuestRating > 0 && (
                  <>
                    {Number.isFinite(hotel.rating) &&
                      hotel.rating >= 1 &&
                      hotel.rating <= 5 && (
                        <span className="text-gray-300" aria-hidden="true">
                          ·
                        </span>
                      )}
                    <GuestRatingBadge
                      rating={effectiveGuestRating}
                      count={effectiveGuestRatingCount}
                      size="sm"
                    />
                  </>
                )}
              </div>
              {distance !== undefined && Number.isFinite(distance) && (
                <p className="text-sm text-gray-600">
                  {distance.toFixed(1)} mi to{' '}
                  {proximityName || 'search location'} · approx.{' '}
                  {Math.round(distance * 20)} min walk
                </p>
              )}
              <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                {HOTEL_AMENETIES.filter((item) =>
                  (hotel.amenities ?? '')
                    .toLowerCase()
                    .includes(item.value.toLowerCase())
                ).map((item) => (
                  <span
                    key={item.value}
                    className="inline-flex items-center gap-2"
                  >
                    {item.icon}
                    {item.label}
                  </span>
                ))}
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-48" />
              <div className="flex gap-2">
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-5 w-16" />
              </div>
            </div>
          )}

          {isLoading ? (
            <div
              className="space-y-2 py-1"
              aria-label="Loading hotel description"
              role="status"
            >
              <span className="sr-only">Loading hotel description…</span>
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-11/12" />
              <Skeleton className="h-3.5 w-4/5" />
            </div>
          ) : description ? (
            <p className="line-clamp-4 text-sm leading-relaxed text-gray-600">
              {description}
            </p>
          ) : null}

          {error && !data && (
            <div role="alert" className="text-sm text-gray-600">
              Unable to load additional hotel details.{' '}
              <button
                className="font-semibold text-primary-600 underline"
                onClick={() => mutate()}
              >
                Try again
              </button>
            </div>
          )}

          <div className="mt-auto flex flex-wrap items-end justify-between gap-4 border-t pt-5">
            {label ? (
              <div>
                <p className="text-xs font-medium text-gray-500">
                  Starting from
                </p>
                <p className="text-2xl font-bold">{label}</p>
                <p className="text-xs text-gray-500">
                  {isBundle ? 'Per traveler' : 'Per stay'}
                </p>
                <p className="text-xs text-gray-500">Includes taxes & fees</p>
              </div>
            ) : (
              <div className="space-y-1">
                <Skeleton className="h-7 w-28" />
                <Skeleton className="h-3 w-20" />
              </div>
            )}
            <Button asChild>
              <Link
                href={`/search-hotel/${hotelId}?eventId=${eventId}&eventLink=${eventLink}&packageType=${packageType}`}
                onClick={() =>
                  useSearchHotelStore.setState({
                    hotelDrawerSelected: { isOpen: false, hotelId: null },
                    isMapFullScreen: false,
                  })
                }
              >
                View Rooms →
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </DialogContent>
  );
}
